/**
 * Beach wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const beachWallpapersPack: ToilePack = {
    slug: 'beach-wallpapers',
    category: 'screens',
    name: 'Beach wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'aerial', label: 'From above', subject: 'a beach seen from straight above, turquoise water meeting pale sand with a curving line of white surf'},
        {key: 'palm', label: 'Palm beach', subject: 'a palm tree leaning over a white-sand beach with clear turquoise water, a small island on the horizon'},
        {key: 'sunset', label: 'Beach sunset', subject: 'a sunset over gentle waves, the sky orange and pink, the sun low and its reflection on the wet sand'},
    ],
    styles: [
        {key: 'drone', label: 'Drone photo', style: 'bright aerial photograph look, crisp detail, natural color, glassy water', palette: 'turquoise, white surf and pale sand', framing: 'Full-bleed, the shore running through the lower two thirds'},
        {key: 'film', label: 'Film grain', style: 'warm vintage film photograph look, grain, faded highlights, soft light leaks', palette: 'faded teal, peach and warm cream', framing: 'Full-bleed, the subject in the lower two thirds'},
        {key: 'gouache', label: 'Gouache', style: 'flat gouache illustration, simple shapes, visible paper texture, a poster feel', palette: 'coral, turquoise and sand yellow', framing: 'Full-bleed, shapes stacked up the screen and sky calm at the top'},
    ],
    directions: ['shells', 'surfer', 'night beach'],
    directionHint: 'try: shells',
    seo: {
        title: 'Beach Wallpapers: From Above, Palm, Sunset',
        metaDescription: 'Beach phone wallpapers: turquoise water from above, a palm over white sand and a sunset over waves, each in three styles.',
        h1: 'Beach wallpapers: water from above, a palm beach, a beach sunset',
        tagline: 'Nine phone wallpapers of one shore in three scenes and three looks.',
        intro: 'Phone wallpapers of the beach: turquoise water from above with white surf, a palm leaning over a tropical shore, and a sunset over gentle waves. Each comes as a bright drone photo look, a warm film-grain look and a flat gouache poster. People who miss summer, or live by the sea, can set them as they are. Type a different scene, such as shells or a night beach, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a summer reminder on a tablet',
            'a seaside home or cafe print',
            'a gift for someone who misses the sea',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three beach scenes: the shore from above, a palm on a tropical beach, and a sunset over waves. Each scene is shown in three styles.'},
            {q: 'Can I change the beach?', a: 'Yes. Type a scene such as shells, a surfer or a night beach; a typed scene is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Drone photo is a bright, sharp aerial look. Film grain is warm and vintage, with grain and light leaks. Gouache is a flat painted poster in coral, turquoise and sand yellow.'},
            {q: 'Are these real photographs?', a: 'No. The drone and film styles have a photo look, but all nine pictures are rendered images.'},
        ],
    },
}
